"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { NEW_IMAGES } from "@/lib/images";

const galleryImages = NEW_IMAGES.gallery.map((src, i) => ({
  src,
  alt: `Cancha de futsal parquet El Bolsón - Big Sports ${i + 1}`,
}));

const DISPLAY_COUNT = 9;

/**
 * Bento placement (md+): 4 columns × 3 rows — large tile top-left, 4 tiles top-right, 4 bottom.
 * Row heights come from `grid-rows` + `minmax()` so cells never collapse to strips.
 */
const BENTO_ITEM_CLASS: Record<number, string> = {
  0: "sm:col-span-2 sm:aspect-[2/1] md:col-span-2 md:row-span-2 md:aspect-auto",
  1: "md:col-start-3 md:row-start-1",
  2: "md:col-start-4 md:row-start-1",
  3: "md:col-start-3 md:row-start-2",
  4: "md:col-start-4 md:row-start-2",
  5: "md:col-start-1 md:row-start-3",
  6: "md:col-start-2 md:row-start-3",
  7: "md:col-start-3 md:row-start-3",
  8: "md:col-start-4 md:row-start-3",
};

export default function GallerySection() {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelectedIndex(null);
      if (e.key === "ArrowLeft") {
        setSelectedIndex((i) => (i === null ? null : (i - 1 + galleryImages.length) % galleryImages.length));
      }
      if (e.key === "ArrowRight") {
        setSelectedIndex((i) => (i === null ? null : (i + 1) % galleryImages.length));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [selectedIndex]);

  const goPrev = () => {
    setSelectedIndex((i) => (i === null ? null : (i - 1 + galleryImages.length) % galleryImages.length));
  };

  const goNext = () => {
    setSelectedIndex((i) => (i === null ? null : (i + 1) % galleryImages.length));
  };

  return (
    <div className="overflow-hidden">
      <ul
        className="grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 sm:gap-3 md:grid-cols-4 md:gap-4 md:grid-rows-[minmax(12rem,auto)_minmax(12rem,auto)_minmax(12rem,auto)]"
      >
        {galleryImages.slice(0, DISPLAY_COUNT).map((img, index) => (
          <li
            key={img.src}
            className={`min-h-0 min-w-0 md:h-full ${BENTO_ITEM_CLASS[index] ?? ""}`}
          >
            <button
              type="button"
              onClick={() => setSelectedIndex(index)}
              className={`group relative w-full overflow-hidden rounded-2xl border border-slate-200/90 bg-slate-100 text-left shadow-sm ring-1 ring-black/4 transition duration-300 hover:z-10 hover:shadow-xl hover:ring-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                index === 0
                  ? "aspect-[4/3] sm:aspect-[2/1] md:aspect-auto md:h-full md:min-h-0"
                  : "aspect-4/3 sm:aspect-4/3 md:aspect-auto md:h-full md:min-h-0"
              }`}
              aria-label={`${t("gallery.viewFullSize")} — ${index + 1}`}
            >
              <Image
                src={img.src}
                alt={img.alt}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                className="object-cover object-[50%_22%] transition duration-500 ease-out group-hover:scale-[1.03]"
                loading={index < 4 ? "eager" : "lazy"}
                quality={78}
              />
              <div
                className={`pointer-events-none absolute inset-0 bg-linear-to-t from-slate-950/45 via-slate-950/5 to-transparent opacity-70 transition duration-300 group-hover:opacity-90 ${index === DISPLAY_COUNT - 1 ? "from-slate-950/55 opacity-90" : ""}`}
                aria-hidden
              />
              {index === DISPLAY_COUNT - 1 && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="rounded-full bg-white/95 px-5 py-2.5 text-sm font-semibold text-slate-900 shadow-lg backdrop-blur-sm md:text-base">
                    {t("gallery.seeMore")}
                  </span>
                </div>
              )}
            </button>
          </li>
        ))}
      </ul>

      {selectedIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/96 backdrop-blur-md safe-area-inset-top safe-area-inset-bottom"
          onClick={() => setSelectedIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label={t("gallery.imageGallery")}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIndex(null);
            }}
            className="absolute right-4 top-4 flex h-11 w-11 min-h-11 min-w-11 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
            aria-label={t("gallery.close")}
          >
            <X className="h-6 w-6" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            className="absolute left-2 top-1/2 flex h-12 w-12 min-h-12 min-w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25 sm:left-4"
            aria-label={t("gallery.previousImage")}
          >
            <ChevronLeft className="h-8 w-8" />
          </button>

          <div
            className="relative flex flex-col items-center px-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-[min(85vh,720px)] w-[min(92vw,1100px)]">
              <Image
                src={galleryImages[selectedIndex].src}
                alt={galleryImages[selectedIndex].alt}
                fill
                sizes="92vw"
                className="rounded-xl object-contain shadow-2xl shadow-black/40"
                quality={88}
                priority
              />
            </div>
            <p className="mt-4 font-medium tabular-nums tracking-wide text-white/85">
              {selectedIndex + 1} <span className="text-white/40">/</span> {galleryImages.length}
            </p>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            className="absolute right-2 top-1/2 flex h-12 w-12 min-h-12 min-w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25 sm:right-4"
            aria-label={t("gallery.nextImage")}
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        </div>
      )}
    </div>
  );
}
